import { apiClient } from '../api/client';

/** Valores em centavos. `available` pode ser negativo: estorno a descontar. */
export interface PartnerBalance {
  pending: number;
  available: number;
  paid: number;
  reversed: number;
}

export interface MyPartnerCoupon {
  code: string;
  name: string;
  isActive: boolean;
  expiresAt: string | null;
  discountType: 'PERCENTAGE' | 'FIXED';
  discountAmount: number;
  commissionType: 'PERCENTAGE' | 'FIXED';
  commissionValue: number;
  includesCredits: boolean;
}

export interface MyPartner {
  nick: string | null;
  status: 'ACTIVE' | 'SUSPENDED';
  holdDays: number;
  since: string;
  coupons: MyPartnerCoupon[];
}

export interface PartnerDailyPoint {
  date: string;
  sales: number;
  revenue: number;
  commission: number;
}

export interface MyPartnerReport {
  from: string;
  to: string;
  sales: number;
  revenue: number;
  commission: number;
  reversedSales: number;
  daily: PartnerDailyPoint[];
  balance: PartnerBalance;
}

export type CommissionState = 'PENDING' | 'AVAILABLE' | 'PAID' | 'REVERSED';

export interface MyCommission {
  id: string;
  kind: 'SALE' | 'REVERSAL';
  state: CommissionState;
  couponCode: string;
  baseAmount: number;
  amount: number;
  availableAt: string;
  reversedAt: string | null;
  createdAt: string;
  /** Mascarado pela API (`Co***`). */
  buyerNick: string | null;
}

export interface MyPayout {
  id: string;
  amount: number;
  method: 'EXTERNAL' | 'STORE_CREDITS';
  reference: string | null;
  commissionCount: number;
  createdAt: string;
}

export interface Page<T> {
  items: T[];
  total: number;
  page: number;
  limit: number;
}

const base = (storeId: string) => `/public/stores/${storeId}/partner/me`;

/**
 * Área do parceiro na vitrine.
 *
 * Nenhuma rota recebe id de parceiro: a API tira o parceiro do login. Quem não
 * é parceiro recebe 404, e `getMe` devolve `null`.
 */
export const partnerService = {
  async getMe(storeId: string): Promise<MyPartner | null> {
    try {
      return await apiClient.get<MyPartner>(base(storeId));
    } catch (error: any) {
      if (error?.response?.status === 404) return null;
      throw error;
    }
  },

  report(storeId: string, period: { from: string; to: string }) {
    return apiClient.get<MyPartnerReport>(`${base(storeId)}/report`, { params: period });
  },

  commissions(storeId: string, params: { status?: CommissionState; page?: number; limit?: number } = {}) {
    return apiClient.get<Page<MyCommission>>(`${base(storeId)}/commissions`, { params });
  },

  payouts(storeId: string, params: { page?: number; limit?: number } = {}) {
    return apiClient.get<Page<MyPayout>>(`${base(storeId)}/payouts`, { params });
  },
};
